import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { plotChapters } from "@/mocks/plot-analysis";
import { articles } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function PlotAnalysis() {
  usePageMeta(
    "剧情解析专题：逐章拆解《色戒》 - 色戒未删减版",
    "《色戒》完整版剧情解析专题，按章节梳理人物关系、关键场景与结尾调度，配合目录联动与延伸阅读，深入理解故事脉络。",
    "色戒剧情解析,色戒完整版解析,剧情脉络,人物关系,结尾解读,专题",
  );

  const [activeId, setActiveId] = useState(plotChapters[0].id);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 },
    );
    plotChapters.forEach((chapter) => {
      const element = document.getElementById(chapter.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(height > 0 ? Math.min(100, (window.scrollY / height) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToChapter = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const currentIndex = plotChapters.findIndex((chapter) => chapter.id === activeId);
  const extension = articles.filter((article) =>
    ["mahjong-table-tension", "ending-light-shadow", "novel-to-screen"].includes(article.id),
  );

  return (
    <SiteLayout>
      <div className="fixed left-0 top-0 z-[55] h-0.5 w-full bg-transparent">
        <div
          className="h-full bg-accent-500/80 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="relative overflow-hidden border-b border-background-800/70 pt-[72px]">
        <img
          src="https://readdy.ai/api/search-image?query=Atmospheric%20cinematic%20banner%20of%20a%20dim%201940s%20Shanghai%20mansion%20corridor%2C%20deep%20crimson%20curtains%2C%20warm%20lamp%20glow%2C%20shadowed%20doorways%2C%20rain%20streaks%20on%20tall%20windows%2C%20drifting%20haze%2C%20moody%20film%20noir%20lighting%2C%20rich%20gold%20and%20dark%20red%20palette%2C%20heavy%20film%20grain%2C%20no%20text&width=1920&height=700&seq=sejie-special-header-01&orientation=landscape"
          alt="《色戒》剧情解析专题视觉"
          title="色戒完整版 - 剧情解析专题"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/90 to-background-950/50" />
        <div className="relative z-10 mx-auto max-w-[1180px] px-4 pb-14 pt-12 md:px-8 md:pb-20 md:pt-16">
          <nav className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-600" />
            <span className="text-foreground-300">剧情解析专题</span>
          </nav>
          <p className="mt-6 font-label text-[10px] uppercase tracking-[0.28em] text-accent-400">
            Special · Plot Analysis
          </p>
          <h1 className="mt-4 max-w-3xl font-heading text-[30px] leading-[1.28] text-foreground-50 md:text-[46px] md:leading-[1.2]">
            逐章拆解《色戒》：
            <br className="hidden sm:block" />
            当沉默成为叙事
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-[1.95] text-foreground-300">
            我们按时间顺序把故事拆成六个章节，从开场牌桌到结尾的空镜，逐段梳理人物关系、空间调度与镜头选择。你可以顺序阅读，也可以直接跳到最在意的段落。
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3 text-[12px] text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-book-open-line text-accent-400" />
              共 6 章
            </span>
            <span className="h-3 w-px bg-foreground-50/20" />
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-accent-400" />
              约 18 分钟读完
            </span>
            <span className="h-3 w-px bg-foreground-50/20" />
            <span>适合二刷前后阅读</span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1180px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[250px_1fr] lg:gap-14">
          <aside>
            <div className="lg:sticky lg:top-[100px]">
              <div className="rounded-lg border border-background-800/80 bg-background-900/40 p-4">
                <h2 className="flex items-center gap-2 font-heading text-sm text-foreground-50">
                  <i className="ri-list-unordered text-accent-400" />
                  章节目录
                </h2>
                <span className="mt-3 block h-px w-full bg-background-800" />
                <ul className="mt-3 space-y-0.5">
                  {plotChapters.map((chapter) => (
                    <li key={chapter.id}>
                      <button
                        type="button"
                        onClick={() => scrollToChapter(chapter.id)}
                        className={`flex w-full cursor-pointer items-start gap-2 rounded-md px-2.5 py-2 text-left text-[12.5px] leading-snug transition-colors ${
                          activeId === chapter.id
                            ? "bg-primary-500/15 text-accent-300"
                            : "text-foreground-400 hover:bg-background-900 hover:text-foreground-100"
                        }`}
                      >
                        <span className="font-label text-[10px] text-foreground-600">
                          {chapter.index}
                        </span>
                        {chapter.title.split("：")[0]}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 rounded-lg border border-primary-900/60 bg-primary-950/50 p-4">
                <p className="text-[11px] leading-relaxed text-primary-100/70">
                  阅读提示：专题内容涉及完整剧情走向，建议在观影后阅读。
                </p>
                <Link
                  to="/guide"
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-accent-300 transition-colors hover:text-accent-200"
                >
                  先看观影指南
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>

          <div>
            <div className="mb-8 flex gap-2 overflow-x-auto pb-1 lg:hidden">
              {plotChapters.map((chapter) => (
                <button
                  key={chapter.id}
                  type="button"
                  onClick={() => scrollToChapter(chapter.id)}
                  className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-2 text-[12px] transition-colors ${
                    activeId === chapter.id
                      ? "border-accent-600/70 bg-accent-500/10 text-accent-300"
                      : "border-background-700 text-foreground-400"
                  }`}
                >
                  {chapter.index} · {chapter.title.split("：")[0]}
                </button>
              ))}
            </div>

            <div className="space-y-12 md:space-y-16">
              {plotChapters.map((chapter) => (
                <section
                  key={chapter.id}
                  id={chapter.id}
                  className="scroll-mt-[100px]"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-accent-500/40 bg-background-900/60 font-label text-[11px] text-accent-300">
                      {chapter.index}
                    </span>
                    <span className="h-px flex-1 bg-background-800" />
                  </div>
                  <h2 className="mt-4 font-heading text-2xl leading-snug text-foreground-50 md:text-[28px]">
                    {chapter.title}
                  </h2>
                  <p className="mt-4 border-l-2 border-primary-600/60 pl-4 text-[15px] leading-[1.95] text-foreground-200">
                    {chapter.lead}
                  </p>
                  <div className="mt-5 space-y-4">
                    {chapter.paragraphs.map((paragraph) => (
                      <p
                        key={paragraph.slice(0, 18)}
                        className="text-[15px] leading-[2.05] text-foreground-400"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {chapter.image ? (
                    <figure className="mt-6 overflow-hidden rounded-lg border border-background-800/80">
                      <img
                        src={chapter.image}
                        alt={`${chapter.title}场景示意图`}
                        title={`${chapter.title} - 色戒剧情解析`}
                        className="aspect-[3/2] w-full object-cover object-top"
                      />
                      <figcaption className="border-t border-background-800 bg-background-900/60 px-4 py-2.5 text-[11px] text-foreground-500">
                        场景示意 · {chapter.title}
                      </figcaption>
                    </figure>
                  ) : null}
                </section>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-800/80 bg-background-900/40 p-4">
              <button
                type="button"
                disabled={currentIndex <= 0}
                onClick={() => {
                  const prev = plotChapters[currentIndex - 1];
                  if (prev) scrollToChapter(prev.id);
                }}
                className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 text-[13px] text-foreground-300 transition-colors hover:border-accent-600/60 hover:text-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line" />
                上一章
              </button>
              <span className="font-label text-[11px] uppercase tracking-[0.2em] text-foreground-600">
                {currentIndex + 1} / {plotChapters.length}
              </span>
              <button
                type="button"
                disabled={currentIndex >= plotChapters.length - 1}
                onClick={() => {
                  const next = plotChapters[currentIndex + 1];
                  if (next) scrollToChapter(next.id);
                }}
                className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 text-[13px] text-foreground-300 transition-colors hover:border-accent-600/60 hover:text-accent-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                下一章
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            <section className="mt-12 rounded-lg border border-background-800/80 bg-background-900/40 p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                为什么值得读这个专题
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                {[
                  {
                    icon: "ri-route-line",
                    title: "按时间顺序",
                    body: "六章对应故事推进的关键节点，清楚看到关系如何一步步变化。",
                  },
                  {
                    icon: "ri-focus-3-line",
                    title: "聚焦镜头细节",
                    body: "不只复述情节，而是解释镜头为什么停在这里、光为什么这样打。",
                  },
                  {
                    icon: "ri-links-line",
                    title: "延伸阅读串联",
                    body: "每章都能继续跳到影评、幕后与版本考据，避免孤立地理解。",
                  },
                ].map((item) => (
                  <div key={item.title}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-accent-500/40 text-accent-300">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <h3 className="mt-3 font-heading text-[15px] text-foreground-50">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground-500">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                  延伸阅读
                </h2>
                <Link
                  to="/category?type=analysis"
                  className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] text-foreground-400 transition-colors hover:text-accent-300"
                >
                  更多解析
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="mt-5 divide-y divide-background-800/80 overflow-hidden rounded-lg border border-background-800/80">
                {extension.map((article) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex items-center gap-4 bg-background-900/30 px-4 py-4 transition-colors hover:bg-background-900"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-background-700 text-foreground-500 transition-colors group-hover:border-accent-600/60 group-hover:text-accent-300">
                      <i className="ri-article-line" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-[14px] text-foreground-100 transition-colors group-hover:text-accent-300">
                        {article.title}
                      </h3>
                      <p className="mt-1 line-clamp-1 text-[12px] text-foreground-500">
                        {article.excerpt}
                      </p>
                    </div>
                    <i className="ri-arrow-right-up-line shrink-0 text-foreground-600 transition-colors group-hover:text-accent-400" />
                  </Link>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col gap-3 rounded-lg border border-primary-900/60 bg-primary-950/50 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[13px] leading-relaxed text-primary-100/75">
                相关专题：版本考据与幕后资料，帮你把剧情放回拍摄语境里。
              </p>
              <Link
                to="/category"
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm text-background-950 transition-colors hover:bg-accent-400"
              >
                进入资料库
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}